import {
  AnimateIconTranslationRight,
  useAnimateIconTranslationRight,
} from '@dreamstack/feature-components/src/layout/AnimateIconTranslation'
import { GridWithGaps } from '@dreamstack/feature-components/src/layout/MainGrid'
import {
  HeadingH1,
  HeadingH4,
} from '@dreamstack/feature-components/src/layout/Texts'
import { ArrowRight } from '@dreamstack/icons/src/icon-components/ArrowRight'
// import type {
//   BlockBigTitleNavigation,
//   Maybe,
//   StaticPage,
// } from '@dreamstack/investors-graphql'
import { assertPopulatedDeep } from '@dreamstack/payload/src/lib/assertPopulated'
import {
  BlockBigTitleNavigation,
  StaticPage,
} from '@dreamstack/payload/src/payload-types'
import { cn, generateIdForText } from '@dreamstack/util'
import { motion } from 'framer-motion'
import Link from 'next/link'
import { forwardRef, type FunctionComponent } from 'react'
import type { additionAttributes } from '../types/ankerAttribute'
import { scrollToTarget } from '../utils/scrollToTarget'

const TextLink: FunctionComponent<
  React.PropsWithChildren<{ onHoverStart?: () => void }>
> = ({ children, onHoverStart }) => (
  <motion.p
    onHoverStart={onHoverStart}
    className={
      'text-accentroAqua-full text-px-16 leading-px-24 flex items-center justify-end font-semibold md:text-px-20 md:leading-px-30'
    }
  >
    {children}
  </motion.p>
)

type DisplayProps = { displayAsColumn?: boolean; className?: string }

const ButtonWrapper: FunctionComponent<
  React.PropsWithChildren<DisplayProps>
> = ({ displayAsColumn, children, className }) => {
  return (
    <div
      className={cn(
        'mt-px-20 flex w-full col-span-full md:col-span-5 md:mt-0',
        displayAsColumn
          ? 'md:col-start-1 md:col-span-full md:mt-px-24 lg:mt-px-48'
          : 'md:items-end md:justify-end',
        className
      )}
    >
      {children}
    </div>
  )
}

const TitleWrapper: FunctionComponent<
  React.PropsWithChildren<DisplayProps & { id?: string }>
> = ({ displayAsColumn, children, id }) => (
  <HeadingH1
    id={id}
    className={cn(
      'col-span-full',
      !displayAsColumn && 'md:mt-px-8 md:col-span-7 lg:mt-px-8'
    )}
  >
    {children}
  </HeadingH1>
)

const SubTitleWrapper: FunctionComponent<
  React.PropsWithChildren<DisplayProps>
> = ({ displayAsColumn, children, className }) => (
  <HeadingH4
    className={cn(
      'font-normal col-span-full',
      !displayAsColumn && 'lg:mt-px-8',
      className
    )}
  >
    {children}
  </HeadingH4>
)

const GridWrapper: FunctionComponent<React.PropsWithChildren<DisplayProps>> = ({
  displayAsColumn,
  children,
  className,
}) => (
  <GridWithGaps className={cn(displayAsColumn && 'grid-cols-6', className)}>
    {children}
  </GridWithGaps>
)

const ButtonLink = forwardRef<
  HTMLAnchorElement,
  React.PropsWithChildren<DisplayProps & { href?: string }>
>(({ displayAsColumn, children, className, href }, ref) => (
  <a
    ref={ref}
    href={href}
    className={cn(!displayAsColumn && 'md:text-right', className)}
  >
    {children}
  </a>
))

ButtonLink.displayName = 'ButtonLink'

export const BigTitleNavigation: FunctionComponent<
  React.PropsWithChildren<{
    block:
      | (Pick<
          BlockBigTitleNavigation,
          'title' | 'subTitle' | 'buttonText' | 'additionalInfoText' | 'url'
        > & {
          staticPageReference?: Partial<StaticPage> | number | null | undefined
        })
      | undefined
    displayAsColumn?: boolean
  }>
> = ({ block, displayAsColumn }) => {
  assertPopulatedDeep(block)
  const showButton = !!block?.url || !!block?.staticPageReference?.slug
  const { onHoverStart, controls } = useAnimateIconTranslationRight()
  if (!block) return null
  const id = generateIdForText(block.title || '')

  const additionAttributes: additionAttributes = {}
  if (block?.url?.startsWith('#')) {
    additionAttributes.onClick = (
      event?: React.MouseEvent<Element, MouseEvent> | undefined
    ) => scrollToTarget(block.url || '', event)
  }

  return (
    <div>
      {block.subTitle && (
        <GridWrapper displayAsColumn={displayAsColumn}>
          <SubTitleWrapper displayAsColumn={displayAsColumn}>
            {block.subTitle}
          </SubTitleWrapper>
        </GridWrapper>
      )}
      <GridWrapper displayAsColumn={displayAsColumn}>
        <TitleWrapper id={id} displayAsColumn={displayAsColumn}>
          {block.title}
        </TitleWrapper>
        <ButtonWrapper displayAsColumn={displayAsColumn}>
          {showButton && (
            <Link
              href={`${block?.url || block?.staticPageReference?.slug}`}
              passHref
              legacyBehavior
              {...additionAttributes}
            >
              <ButtonLink displayAsColumn={displayAsColumn}>
                <TextLink onHoverStart={onHoverStart}>
                  <span>{block.buttonText}</span>
                  <AnimateIconTranslationRight animate={controls}>
                    <ArrowRight className={cn('w-px-32 h-px-32 ml-px-20')} />
                  </AnimateIconTranslationRight>
                </TextLink>
              </ButtonLink>
            </Link>
          )}
        </ButtonWrapper>
        {block.additionalInfoText && (
          <SubTitleWrapper
            displayAsColumn={displayAsColumn}
            className={cn('row-start-2 pt-px-24')}
          >
            <span className={cn('whitespace-pre-wrap')}>
              {block.additionalInfoText}
            </span>
          </SubTitleWrapper>
        )}
      </GridWrapper>
    </div>
  )
}
